import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { AccountInfo } from '@azure/msal-browser';
import { API_BASE_URL, ONELOGIN_AUDIENCE, ONELOGIN_AUTHORITY } from '../utils/constants';
import { Authorization, SelectedProject } from './Authorization.interfaces';
import { msalInstance, loginRequest } from './msalConfig';
import axios from 'axios';

const oidcConfig = {
    authority: ONELOGIN_AUTHORITY,
    client_id: ONELOGIN_AUDIENCE,
    redirect_uri: window.location.origin + '/auth',
    response_type: 'code',
    scope: 'openid profile email',
    post_logout_redirect_uri: window.location.origin + '/',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
};

export const userManager = new UserManager(oidcConfig);

interface AuthContextType {
    user: User | null;
    msalAccount: AccountInfo | null;
    isAuthenticated: boolean;
    token: string | null;
    signinRedirect: () => void;
    msalSignIn: () => Promise<void>;
    signoutRedirect: () => void;
    refreshUser: () => Promise<void>;
    authorization: Authorization | null;
    getValidAccessToken: () => Promise<string | null>;
    selectedProject: SelectedProject | null;
    setSelectedProject: (project: SelectedProject) => void;
    clearSelectedProject: () => void;
    isProjectSelectionRequired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [msalAccount, setMsalAccount] = useState<AccountInfo | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const msalInitialized = useRef(false);
    const [authorization, setAuthorization] = useState<Authorization | null>(null);
    const [selectedProject, setSelectedProjectState] = useState<SelectedProject | null>(null);
    const [isProjectSelectionRequired, setIsProjectSelectionRequired] = useState(false);

    // Load selected project from localStorage
    const loadSelectedProject = React.useCallback(() => {
        try {
            const stored = localStorage.getItem('selectedProject');
            if (stored) {
                return JSON.parse(stored) as SelectedProject;
            }
        } catch (error) {
            console.error('Error loading selected project from localStorage:', error);
            localStorage.removeItem('selectedProject');
        }
        return null;
    }, []);

    // Save selected project to localStorage
    const setSelectedProject = React.useCallback((project: SelectedProject) => {
        setSelectedProjectState(project);
        localStorage.setItem('selectedProject', JSON.stringify(project));
        setIsProjectSelectionRequired(false);
    }, []);

    // Clear selected project
    const clearSelectedProject = React.useCallback(() => {
        setSelectedProjectState(null);
        localStorage.removeItem('selectedProject');
        setIsProjectSelectionRequired(true);
    }, []);

    // Validate selected project against user's authorized projects
    const validateSelectedProject = React.useCallback((auth: Authorization, storedProject: SelectedProject | null) => {
        if (!storedProject) {
            setIsProjectSelectionRequired(true);
            return;
        }

        const matchingProject = auth.projects.find(p => p.projectId === storedProject.projectId);
        if (matchingProject) {
            const updatedProject = {
                projectId: matchingProject.projectId,
                projectName: matchingProject.projectName,
                dbname: matchingProject.projectDatabaseName,
                role: matchingProject.role,
                readonly: matchingProject.readonly,
                showSubstantive: matchingProject.showSubstantive,
            };
            setSelectedProjectState(updatedProject);
            localStorage.setItem('selectedProject', JSON.stringify(updatedProject));
            setIsProjectSelectionRequired(false);
        } else {
            clearSelectedProject();
        }
    }, [clearSelectedProject]);

    // Shared authorization fetch – works for both OIDC and MSAL users
    const fetchAuthorization = React.useCallback(async (email: string, accessToken: string | null) => {
        try {
            const response = await axios.post<Authorization>(
                `${API_BASE_URL}/auth/user_authorization`,
                { userEmail: email },
                {
                    headers: {
                        'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                        'Content-Type': 'application/json',
                    }
                }
            );
            if (!response.data || !response.data.userId) {
                setAuthorization({ userId: 0, system_role: '', swca_user: true, projects: [] });
                setIsProjectSelectionRequired(true);
                return;
            }
            setAuthorization(response.data);

            const storedProject = loadSelectedProject();

            if (response.data.projects.length === 1) {
                const singleProject = response.data.projects[0];
                const projectToSelect = {
                    projectId: singleProject.projectId,
                    projectName: singleProject.projectName,
                    dbname: singleProject.projectDatabaseName,
                    role: singleProject.role,
                    readonly: singleProject.readonly,
                    showSubstantive: singleProject.showSubstantive,
                };
                setSelectedProject(projectToSelect);
            } else {
                validateSelectedProject(response.data, storedProject);
            }
        } catch (error) {
            setAuthorization(null);
            setIsProjectSelectionRequired(true);
        }
    }, [loadSelectedProject, validateSelectedProject, setSelectedProject]);

    const refreshUser = React.useCallback(async () => {
        const loadedUser = await userManager.getUser();
        if (loadedUser && !loadedUser.expired) {
            setUser(loadedUser);
            setToken(loadedUser.access_token || loadedUser.id_token || null);
            await fetchAuthorization(
                loadedUser.profile.email || loadedUser.profile.preferred_username || '',
                loadedUser.access_token
            );
        } else {
            setUser(null);
            setToken(null);
            setAuthorization(null);
        }
    }, [fetchAuthorization]);

    const getValidAccessToken = React.useCallback(async () => {
        // Prefer MSAL token when an MSAL account is active
        if (msalAccount) {
            try {
                const response = await msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account: msalAccount,
                });
                const msalToken = response.accessToken || response.idToken || null;
                setToken(msalToken);
                return msalToken;
            } catch {
                setMsalAccount(null);
                setToken(null);
                setAuthorization(null);
                return null;
            }
        }

        // Fall back to OIDC
        let loadedUser = await userManager.getUser();
        if (!loadedUser || loadedUser.expired) {
            try {
                loadedUser = await userManager.signinSilent();
                if (loadedUser) {
                    setUser(loadedUser);
                    setToken(loadedUser.access_token || loadedUser.id_token || null);
                    await fetchAuthorization(
                        loadedUser.profile.email || loadedUser.profile.preferred_username || '',
                        loadedUser.access_token
                    );
                } else {
                    setUser(null);
                    setToken(null);
                    setAuthorization(null);
                    return null;
                }
            } catch (err) {
                setUser(null);
                setToken(null);
                setAuthorization(null);
                return null;
            }
        }
        if (loadedUser) {
            return loadedUser.access_token || loadedUser.id_token || null;
        }
        return null;
    }, [fetchAuthorization, msalAccount]);

    // One-time initialization: handle MSAL redirects and restore any existing session
    useEffect(() => {
        const initializeAuth = async () => {
            // MSAL must be initialized before any other MSAL operations
            if (!msalInitialized.current) {
                await msalInstance.initialize();
                msalInitialized.current = true;
            }

            // Process any pending MSAL redirect response (e.g. returning from Entra login)
            const msalResponse = await msalInstance.handleRedirectPromise();
            if (msalResponse?.account) {
                msalInstance.setActiveAccount(msalResponse.account);
                const msalToken = msalResponse.accessToken || msalResponse.idToken || null;
                const email =
                    (msalResponse.account.idTokenClaims as Record<string, unknown>)?.email as string
                    || msalResponse.account.username;
                setMsalAccount(msalResponse.account);
                setToken(msalToken);
                await fetchAuthorization(email, msalToken);
                return;
            }

            // Restore an existing MSAL session from cache
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                const account = accounts[0];
                msalInstance.setActiveAccount(account);
                try {
                    const silentResponse = await msalInstance.acquireTokenSilent({
                        ...loginRequest,
                        account,
                    });
                    const msalToken = silentResponse.accessToken || silentResponse.idToken || null;
                    const email =
                        (account.idTokenClaims as Record<string, unknown>)?.email as string
                        || account.username;
                    setMsalAccount(account);
                    setToken(msalToken);
                    await fetchAuthorization(email, msalToken);
                    return;
                } catch {
                    // Silent acquisition failed; fall through to OIDC check
                }
            }

            // Fall back to OIDC (SWCA) session check
            await refreshUser();
        };

        initializeAuth().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sign in via Microsoft Entra ID (non-SWCA users)
    const msalSignIn = async () => {
        if (!msalInitialized.current) {
            await msalInstance.initialize();
            msalInitialized.current = true;
        }
        await msalInstance.loginRedirect(loginRequest);
    };

    let signinInProgress = false;

    // Sign in via OneLogin (SWCA users)
    const signinRedirect = () => {
        if (signinInProgress) return;
        signinInProgress = true;
        userManager.signinRedirect().finally(() => {
            signinInProgress = false;
        });
    };

    const signoutRedirect = () => {
        if (msalAccount) {
            setMsalAccount(null);
            setToken(null);
            setAuthorization(null);
            clearSelectedProject();
            msalInstance.logoutRedirect({
                account: msalAccount,
                postLogoutRedirectUri: window.location.origin + '/',
            });
        } else {
            userManager.signoutRedirect();
        }
    };

    const isAuthenticated = !!user || !!msalAccount;

    return (
        <AuthContext.Provider value={{
            user,
            msalAccount,
            isAuthenticated,
            token,
            signinRedirect,
            msalSignIn,
            signoutRedirect,
            refreshUser,
            authorization,
            getValidAccessToken,
            selectedProject,
            setSelectedProject,
            clearSelectedProject,
            isProjectSelectionRequired
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
